import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { Heading } from '../Heading/Heading';

import css from './ProductVariantsInfo.module.css';

/**
 * Which version of a product an order is for — "Size: Medium", "Volume: 100ml".
 *
 * Shown to the shopper while they pay and to both parties afterwards, so the
 * brand knows what to pack. The entries are read from the transaction's (or the
 * pending order's) own data rather than from the hosted config, so an order
 * placed before the vendor edited their options still reads correctly. See
 * util/productVariants.js.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<Object>} props.entries - [{ key, label, value, valueLabel }]
 * @param {boolean} [props.showHeading=true] - Whether to render the section heading
 * @param {string} [props.headingId] - Message id for the heading
 * @returns {JSX.Element|null} null when the order has no variants
 */
const ProductVariantsInfo = props => {
  const {
    className,
    rootClassName,
    entries,
    showHeading = true,
    headingId = 'ProductVariantsInfo.heading',
  } = props;
  const shownEntries = (Array.isArray(entries) ? entries : []).filter(
    entry => entry?.valueLabel || entry?.value
  );

  if (shownEntries.length === 0) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      {showHeading ? (
        <Heading as="h3" rootClassName={css.heading}>
          <FormattedMessage id={headingId} />
        </Heading>
      ) : null}
      <dl className={css.list}>
        {shownEntries.map(entry => (
          <div key={entry.key || entry.label} className={css.row}>
            <dt className={css.label}>{entry.label}</dt>
            <dd className={css.value}>{entry.valueLabel || entry.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

export default ProductVariantsInfo;
